import React, { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box } from '@node-space/storybook-components/dist/Box'
import CheckBox from '@node-space/storybook-components/dist/CheckBox'
import Icon from '@node-space/storybook-components/dist/Icon'
import { Input } from '@node-space/storybook-components/dist/Input'
import Input2FA from '@node-space/storybook-components/dist/Input2FA'
import Loader from '@node-space/storybook-components/dist/Loader'
import Modal, {
  ModalActions,
  ModalNavBar,
  ModalScrollable,
} from '@node-space/storybook-components/dist/Modal'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import Text from '@node-space/storybook-components/dist/Text'
import { logSentryError } from '@node-space/utils'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { Approval, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import {
  useApprovalWorkflowActionPreRequest,
  useApprovalWorkflowChallengeRequest,
} from 'hooks/mutations/useApprovalDecisionMutation'
import { useToastContext } from 'hooks/useToastContext'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { ApprovalStatuses } from 'types/approvals'
import { ErrorResponse } from 'types/types'

type ApprovalDecision = keyof typeof Approval.DECISION

interface ApprovalDecisionModalProps {
  decision: ApprovalDecision
  handleCloseApprovalDecisionModal: () => void
  selectedItems?: unknown[]
}

const ApprovalDecisionModal = ({
  decision,
  handleCloseApprovalDecisionModal,
  selectedItems = [],
}: ApprovalDecisionModalProps) => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const addToast = useToastContext()

  const currentAccountReference = currentAccount?.reference
  const [approvalDecisionFormState, setApprovalDecisionFormState] = useState({
    hasReviewedPayment: false,
    rejectionReason: '',
    challengeCode: '',
    twoFactorCode: '',
    twoFactorCodeError: false,
  })

  const { mutate: approvalWorkflowActionPreRequest, isLoading: isLoadingPreRequest } =
    useApprovalWorkflowActionPreRequest()

  const {
    mutate: approvalWorkflowChallengeRequest,
    isLoading: isLoadingPostRequest,
    isSuccess: isSuccessPostRequest,
  } = useApprovalWorkflowChallengeRequest()

  const approvalReferencesPayload = () => {
    const correlationKeys = []
    selectedItems?.forEach((selectedItem: { correlationKey?: string }) => {
      correlationKeys.push(selectedItem?.correlationKey)
    })
    return correlationKeys
  }

  const handleReviewPayment = () => {
    // toggle for decision reject with a rejection reason
    approvalDecisionFormState?.rejectionReason &&
      setApprovalDecisionFormState({
        ...approvalDecisionFormState,
        hasReviewedPayment: false,
      })

    if (
      !approvalDecisionFormState?.hasReviewedPayment ||
      approvalDecisionFormState?.rejectionReason
    ) {
      approvalWorkflowActionPreRequest(
        {
          action: decision,
          reference: approvalReferencesPayload(),
          ...(decision === Approval.DECISION.REJECT && {
            rejectionReason: approvalDecisionFormState?.rejectionReason,
          }),
        },
        {
          onError: error => {
            // pre condition to get challengeCode value
            if (error?.status === 428) {
              setApprovalDecisionFormState({
                ...approvalDecisionFormState,
                hasReviewedPayment: !approvalDecisionFormState?.hasReviewedPayment,
                challengeCode: error?.data?.challenge?.value,
              })
            } else {
              addToast({
                title: t('oopsSomethingWentWrongContracted'),
                state: 'error',
                delay: 5,
              })
              logSentryError('Error from ApprovalDecisionModal - handleReviewPayment', error)
            }
          },
        }
      )
    } else {
      // toggle for decision accept or reject without a rejection reason
      !approvalDecisionFormState?.rejectionReason &&
        setApprovalDecisionFormState({
          ...approvalDecisionFormState,
          hasReviewedPayment: !approvalDecisionFormState?.hasReviewedPayment,
        })
    }
  }

  const handleApprovalDecisionChallenge = () => {
    approvalWorkflowChallengeRequest(
      {
        challenge: approvalDecisionFormState?.challengeCode,
        tokenResponse: approvalDecisionFormState?.twoFactorCode,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(
            reactQueryKeys.approvalsTransactions(currentAccountReference, ApprovalStatuses.PENDING)
          )
        },
        onError: (error: ErrorResponse) => {
          if (error?.status === 403 && !error?.data?.code) {
            setApprovalDecisionFormState({ ...approvalDecisionFormState, twoFactorCodeError: true })
          } else {
            addToast({
              title: t('oopsSomethingWentWrongContracted'),
              state: 'error',
              delay: 5,
            })
            logSentryError(
              'Error from ApprovalDecisionModal - handleApprovalDecisionChallenge',
              error
            )
          }
        },
      }
    )
  }

  const selectedItemsCount = selectedItems?.length

  const decisionWord =
    decision === Approval.DECISION.APPROVE ? t('approvals.approved') : t('approvals.rejected')

  useEffect(() => {
    if (approvalDecisionFormState?.twoFactorCode?.length === TWO_FA_REQUIRED_LENGTH) {
      handleApprovalDecisionChallenge()
    }
  }, [approvalDecisionFormState.twoFactorCode])

  return (
    <Modal closeOnBackgroundClick={false} closeModal={handleCloseApprovalDecisionModal} visible>
      {/* TODO: extract some of the below to smaller components */}
      {isSuccessPostRequest ? (
        <>
          <StatusCard
            status="success"
            headline={t('approvals.decisionSuccessHeadline', {
              paymentCount: selectedItemsCount,
              payment: selectedItemsCount > 1 ? t('approvals.payments') : t('approvals.payment'),
              action: decisionWord,
            })}
            description={t('approvals.decisionSuccessDescription', { tabName: decisionWord })}
            borderless
          />
          <ModalActions
            actions={[
              {
                children: t('done'),
                secondary: true,
                onClick: () => {
                  handleCloseApprovalDecisionModal()
                  queryClient.invalidateQueries(
                    reactQueryKeys.approvalsTransactions(
                      currentAccountReference,
                      ApprovalStatuses.PENDING
                    )
                  )
                  navigate(`${PathNames.APPROVALS}?tab=${ApprovalStatuses.PENDING}`, {
                    replace: true,
                  })
                },
              },
              {
                children: t('approvals.goToTab', { tabName: decisionWord }),
                onClick: () => {
                  handleCloseApprovalDecisionModal()
                  navigate(`${PathNames.APPROVALS}?tab=${decisionWord.toUpperCase()}`, {
                    replace: true,
                  })
                },
              },
            ]}
          />
        </>
      ) : (
        <>
          <ModalNavBar hasDivider={false} onClose={handleCloseApprovalDecisionModal} />
          <ModalScrollable>
            <Box flex direction="col" alignItems="center" justifyContent="center">
              <Icon color="inherit" name="HourglassIcon" />
              <Text align="center" className="pt-3" weight="semibold">
                {t('approvals.decisionQuestionIntro')} {`${decision?.toLowerCase()} `}
                {selectedItemsCount > 1
                  ? t('approvals.thesePayments', { paymentCount: selectedItemsCount })
                  : t('approvals.thisPayment')}
              </Text>
              <Box paddingT={28} paddingB={12}>
                <CheckBox
                  isLoading={
                    isLoadingPreRequest &&
                    !approvalDecisionFormState?.rejectionReason &&
                    !approvalDecisionFormState?.hasReviewedPayment
                  }
                  label={t('approvals.reviewNote', {
                    paymentPronoun:
                      selectedItemsCount > 1 ? t('approvals.each') : t('approvals.this'),
                  })}
                  value={approvalDecisionFormState?.hasReviewedPayment}
                  onChange={handleReviewPayment}
                />
              </Box>
            </Box>
            {decision === Approval.DECISION.REJECT && (
              <Box paddingX={24} paddingY={4}>
                <Input
                  label={t('approvals.reasonForRejection')}
                  placeholder={t('approvals.reason')}
                  rightLabel={t('optionalLabel')}
                  value={approvalDecisionFormState?.rejectionReason}
                  onChange={e => {
                    setApprovalDecisionFormState({
                      ...approvalDecisionFormState,
                      rejectionReason: e.target.value,
                    })
                  }}
                  onBlur={handleReviewPayment}
                />
              </Box>
            )}
            {isLoadingPostRequest ? (
              <Box paddingT={24} paddingB={32} flex justifyContent="center">
                <Loader size="medium" />
              </Box>
            ) : (
              <Box paddingX={24} paddingB={32} direction="col" alignItems="center">
                <Text size="sm" className="relative top-3">
                  {t('authCodeConfirm')}
                </Text>
                <Input2FA
                  testid="approval-decision-2FA-input"
                  disabled={!approvalDecisionFormState?.hasReviewedPayment}
                  onChange={value =>
                    setApprovalDecisionFormState({
                      ...approvalDecisionFormState,
                      twoFactorCode: value,
                    })
                  }
                  error={!!approvalDecisionFormState?.twoFactorCodeError}
                  errorText={t('incorrectOtpCode')}
                />
              </Box>
            )}
          </ModalScrollable>
        </>
      )}
    </Modal>
  )
}

export default ApprovalDecisionModal
